import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CitywireInternalAccountsService } from '@tpapa/citywire-internal-accounts-angular';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { filter, switchMap, take, tap } from 'rxjs/operators';
import { UserAction } from 'src/app/content-management/shared/enums/user-action';
import { UserEvent } from 'src/app/content-management/shared/enums/user-event';
import { SignalrService } from 'src/app/content-management/shared/services/signalr.service';
import { UserAlert } from 'src/app/core/models/i-user-alert';
import { UserState } from 'src/app/core/models/i-user-state';
import { UserListToastrService } from '../../services/custom-toastr-service';
import { FormGroup } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
  selector: 'app-active-users',
  templateUrl: './active-users.component.html',
  styleUrls: ['./active-users.component.scss'],
})
export class ActiveUsersComponent implements OnInit, OnDestroy {
  @Input() contentId: string;
  @Input() form: FormGroup;
  @Input() formReady: Observable<boolean>;

  username: string;
  email: string;
  formAltered: boolean = false;
  subscriptions: Subscription[] = [];
  users$: Observable<UserState[]>;
  usersOnPage: UserState[] = [];

  constructor(
    private signalRService: SignalrService,
    private accountsService: CitywireInternalAccountsService,
    private userListToastrService: UserListToastrService,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer
  ) {}
  ngOnInit() {
    const initSubsciption = this.signalRService.areListenersReady$
      .pipe(
        filter((areReady) => areReady),
        switchMap(() => {
          return this.accountsService.currentUser.pipe(
            tap((x) => {
              this.username = x.username.toLowerCase();
              this.email = x.email.toLowerCase();
            })
          );
        }),
        take(1),
        switchMap((user) => {
          this.users$ = this.signalRService.users$;
          this.signalRService.userJoinedPage(
            user.username,
            this.email,
            this.contentId
          );
          return this.users$;
        })
      )
      .subscribe({
        next: (userList: UserState[]) => {
          this.handleListPopUp(userList);
        },
      });

    const userSubscription = this.signalRService.userAlert$.subscribe({
      next: (x: UserAlert) => {
        this.handleUserAlert(x);
      },
    });

    const readySubscription = this.formReady
      .pipe(
        filter((isReady: boolean) => isReady),
        switchMap(() => this.form.valueChanges)
      )
      .subscribe(() => {
        if (this.form.dirty && !this.formAltered) {
          this.formAltered = true;
          this.userEdited(this.username);
        }
      });

    this.subscriptions.push(
      initSubsciption,
      userSubscription,
      readySubscription
    );
  }

  handleUserAlert(alert: UserAlert) {
    if (alert.contentId === this.contentId && alert.username != this.username) {
      if (alert.action === UserEvent.saved) {
        this.userListToastrService.clearToastr();
        this.userListToastrService.showUserList(
          '',
          this.usersOnPage,
          alert.username
        );
      }
    }
  }

  handleListPopUp(userList: UserState[]) {
    this.usersOnPage = userList.filter((x) => x.username !== this.username);

    if (this.usersOnPage && this.usersOnPage.length !== 0) {
      this.userListToastrService.showUserList(
        'Also on this page:',
        this.usersOnPage
      );
    } else {
      // If the list returned does not contain any users clear any toastrs
      this.toastr.clear();
    }
  }

  userEdited(username: string) {
    this.signalRService.userEditedPage(username, this.contentId);
  }

  getUserAction(action: number) {
    return UserAction[action];
  }

  sanitise(email: string) {
    let url = `MSTeams:/l/chat/0/0?users=${email}`;
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  ngOnDestroy() {
    this.toastr.clear();
    this.userListToastrService.clearToastr();
    this.usersOnPage = [];
    this.signalRService.userLeftPage(this.username, this.contentId);
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
